import * as z from 'zod';

import type {EmbroideryTextOptionCoreFragment} from '@/modules/commerce/embroidery-text/query';
import type {ItemForPriceFragment} from '@/modules/commerce/item/query';

export const embroideryTextConfigurationSchema = z
	.object({
		colorId: z.string().nullable(),
		fontId: z.string(),
		text: z.string().nullable(),
	})
	.nullable();

export type EmbroideryTextConfiguration = z.infer<typeof embroideryTextConfigurationSchema>;

export function getDefaultEmbroideryTextConfiguration(
	_option: EmbroideryTextOptionCoreFragment,
): EmbroideryTextConfiguration {
	return null;
}

export function getInitialEmbroideryTextConfiguration(
	option: EmbroideryTextOptionCoreFragment,
): EmbroideryTextConfiguration {
	const fontId = option.defaultFont?._id ?? option.collection?.fonts?.at(0)?._id;
	if (!fontId) {
		return null;
	}

	return {
		colorId: option.colors?.at(0)?._id ?? null,
		fontId,
		text: option.defaultText ?? '',
	};
}

export function embroideryTextConfigurationFromFormData(
	option: EmbroideryTextOptionCoreFragment,
	prefix: string,
	formData: FormData,
): EmbroideryTextConfiguration {
	if (!option.available) {
		return null;
	}

	const enabled = formData.get(`${prefix}`) === 'enabled';

	if (!enabled) {
		return null;
	}

	let config: EmbroideryTextConfiguration = getInitialEmbroideryTextConfiguration(option);

	const colorId = formData.get(`${prefix}[colorId]`)?.toString() ?? null;
	const fontId = formData.get(`${prefix}[fontId]`)?.toString() ?? null;

	if (fontId) {
		config = {
			colorId,
			fontId,
			text: formData.get(`${prefix}[text]`)?.toString() ?? '',
		};
	}

	return config;
}

export function getItemsForEmbroideryTextConfiguration(
	option: EmbroideryTextOptionCoreFragment,
	configuration: EmbroideryTextConfiguration,
): ItemForPriceFragment[] {
	const items: ItemForPriceFragment[] = [];

	if (!configuration) {
		return items;
	}

	const {configuredFont} = getConfiguredEmbroideryText(option, configuration);

	if (configuredFont && option.items && option.items.length > 0) {
		items.push(...option.items);
	}

	return items;
}

export function getConfiguredEmbroideryText(
	option: EmbroideryTextOptionCoreFragment,
	configuration: EmbroideryTextConfiguration,
) {
	if (!option.available || !configuration) {
		return {
			configuredFont: null,
			configuredColor: null,
		};
	}

	const configuredFont = option.collection?.fonts?.find(
		(font) => font._id === configuration.fontId,
	);

	const configuredColor = option.colors?.find((color) => color._id === configuration.colorId);

	if (!configuredFont) {
		return {
			configuredFont: null,
			configuredColor,
		};
	}

	return {
		configuredFont,
		configuredColor,
	};
}
